<template>
  <div v-if="isAdmin" class="users-list-page" :class="{ 'twitch-theme': isTwitchUser }">
    <div class="row users-list-container">
      <div class="col-12">
        <card>
          <div slot="header" class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <h4 class="card-title mb-0 mr-3">{{ tt("usersList.title", "Usuarios") }}</h4>
              <div v-if="impersonation" class="badge badge-warning">
                <i class="ti-user" style="font-size: 11px; margin-right: 4px;"></i>
                {{ tt("usersList.impersonating", "Suplantando") }}: {{ impersonation.userId }}
              </div>
            </div>
            <div>
              <button
                class="btn users-export-button mr-2"
                :disabled="isLoading || users.length === 0"
                @click="exportUsersCsv"
                title="Exportar usuarios"
                aria-label="Exportar usuarios"
              >
                <i class="ti-download"></i>
              </button>
              <button
                v-if="impersonation"
                class="btn btn-warning mr-2"
                @click="stopImpersonation"
                :title="tt('usersList.stopImpersonationTitle', 'Detener suplantación de este usuario')"
              >
                <i class="ti-back-left"></i> {{ tt("usersList.stopImpersonation", "Detener suplantación") }}
              </button>
              <button
                class="btn btn-primary"
                @click="openCreateModal"
              >
                <i class="ti-plus"></i> {{ tt("usersList.addUser", "Agregar usuario") }}
              </button>
            </div>
          </div>
          <div slot="raw-content" class="users-table-wrapper">
            <div v-if="!isLoading && users.length > 0" class="users-search-toolbar">
              <div class="users-search-toolbar-controls">
                <div class="users-search-input-wrap">
                  <i class="ti-search"></i>
                  <input
                    v-model="usersSearchInput"
                    type="text"
                    class="users-search-input"
                    :placeholder="tt('usersList.searchUsersPlaceholder', 'Buscar por usuario o email')"
                    @focus="showUsersSearchSuggestions = true"
                    @input="showUsersSearchSuggestions = true"
                    @blur="hideUsersSearchSuggestions"
                  />
                  <div
                    v-if="showUsersSearchSuggestions && usersSearchSuggestions.length > 0"
                    class="users-search-suggestions"
                  >
                    <button
                      v-for="user in usersSearchSuggestions"
                      :key="`users-search-suggestion-${user.id}`"
                      type="button"
                      class="users-search-suggestion"
                      @mousedown.prevent="selectUsersSearchSuggestion(user)"
                    >
                      <span class="users-search-suggestion-avatar">{{ getUserSearchInitial(user) }}</span>
                      <span class="users-search-suggestion-copy">
                        <strong>{{ user.username || "N/A" }}</strong>
                        <small>{{ getUserEmail(user) || "N/A" }}</small>
                      </span>
                    </button>
                  </div>
                </div>
                <div class="users-plan-filter-wrap">
                  <label class="users-plan-filter-label" for="users-plan-filter">{{ tt("usersList.planFilter", "Plan") }}</label>
                  <select id="users-plan-filter" v-model="planFilter" class="users-plan-filter-select">
                    <option value="all">{{ tt("usersList.planFilterAll", "All") }}</option>
                    <option value="free">{{ tt("usersList.planFilterFree", "Free") }}</option>
                    <option value="non-free">{{ tt("usersList.planFilterNonFree", "Non-Free") }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="!isLoading && filteredUsers.length > 0" class="table-responsive users-table-responsive">
              <paper-table :data="filteredUsers" :columns="tableColumns" type="hover" class="users-table">
                <template slot="columns">
                  <th class="col-id">{{ tt("usersList.id", "ID") }}</th>
                  <th class="col-username">{{ tt("usersList.username", "Usuario") }}</th>
                  <th class="col-phone">{{ tt("usersList.phone", "Teléfono") }}</th>
                  <th class="col-age">{{ tt("usersList.ageRange", "Edad") }}</th>
                  <th class="col-email">{{ tt("usersList.email", "Email") }}</th>
                  <th class="col-origin">{{ tt("usersList.origin", "Origen") }}</th>
                  <th class="col-plan">{{ tt("usersList.plan", "Plan") }}</th>
                  <th class="col-admin">{{ tt("usersList.admin", "Admin") }}</th>
                  <th class="col-timezone">{{ tt("usersList.timezone", "Zona horaria") }}</th>
                  <th class="col-created">{{ tt("usersList.createdAt", "Creado") }}</th>
                  <th class="col-actions">{{ tt("usersList.actions", "Acciones") }}</th>
                </template>
                <template slot-scope="{ row }">
                  <td class="users-table-id">{{ row.id }}</td>
                  <td class="users-table-username">
                    <span class="username-cell">{{ row.username }}</span>
                  </td>
                  <td class="users-table-phone">{{ getUserPhone(row) || "N/A" }}</td>
                  <td class="users-table-age">{{ row.user_age_range || "N/A" }}</td>
                  <td class="users-table-email">{{ getUserEmail(row) || "N/A" }}</td>
                  <td class="users-table-origin" :title="getSignupOriginTitle(row)">
                    <span class="users-origin-badge" :class="getSignupOriginClass(row)">
                      {{ getSignupOriginLabel(row) }}
                    </span>
                  </td>
                  <td class="users-table-plan">
                    <span
                      class="users-plan-badge"
                      :class="{ 'users-plan-badge--paid': !isUserOnFreePlan(row) }"
                    >
                      {{ row.plan_label || "Free" }}
                    </span>
                  </td>
                  <td>
                    <span v-if="(row.use_role || '').toLowerCase() === 'admin'" class="badge badge-success">{{ tt("common.yes", "Sí") }}</span>
                    <span v-else class="badge badge-secondary">{{ tt("common.no", "No") }}</span>
                  </td>
                  <td>{{ row.timezone || 'N/A' }}</td>
                  <td class="users-table-date">{{ formatDate(row.created_at) }}</td>
                  <td class="users-table-actions">
                    <div class="action-buttons">
                      <button
                        v-if="canImpersonateUsers()"
                        class="btn btn-sm btn-action btn-action-impersonate"
                        :disabled="isImpersonatingRow(row)"
                        @click="impersonateUser(row)"
                        :title="tt('usersList.loginAsUser', 'Entrar como usuario')"
                      >
                        <i class="ti-key"></i>
                      </button>
                      <button
                        class="btn btn-sm btn-action btn-action-tokens"
                        :disabled="isImpersonatingRow(row)"
                        @click="openGrantTokensModal(row)"
                        :title="tt('usersList.grantTokens', 'Agregar tokens PAYG')"
                      >
                        <i class="ti-wallet"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-action btn-action-qa"
                        @click="openOnboardingQaModal(row)"
                        :title="tt('usersList.onboardingQaButtonTitle', 'Respuestas del onboarding')"
                      >
                        <i class="ti-comment-alt"></i>
                      </button>
                      <button
                        class="btn btn-sm btn-action btn-action-edit"
                        @click="openEditModal(row)"
                        :title="tt('common.edit', 'Editar')"
                      >
                        <i class="ti-pencil"></i>
                      </button>
                      <button
                        class="btn btn-sm btn-action btn-action-delete"
                        @click="confirmDelete(row)"
                        :title="tt('common.delete', 'Eliminar')"
                      >
                        <i class="ti-trash"></i>
                      </button>
                    </div>
                  </td>
                </template>
              </paper-table>
            </div>
            <div v-if="isLoading" class="users-table-state users-table-loading">
              <i class="ti-reload ti-spin"></i>
              <p>{{ tt("usersList.loadingUsers", "Cargando usuarios...") }}</p>
            </div>
            <div v-else-if="users.length > 0 && filteredUsers.length === 0" class="users-table-state users-table-empty">
              <i class="ti-search"></i>
              <p>{{ tt("usersList.noUsersMatchFilter", "Ningún usuario coincide con la búsqueda.") }}</p>
            </div>
            <div v-else-if="users.length === 0" class="users-table-state users-table-empty">
              <i class="ti-user"></i>
              <p>{{ tt("usersList.noUsersFound", "No se encontraron usuarios.") }}</p>
              <span class="users-table-empty-hint">{{ tt("usersList.emptyHint", "Haz clic en \"Agregar usuario\" para crear uno.") }}</span>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Create/Edit User Modal -->
    <body-portal>
      <div
        class="modal fade"
        :class="{ show: showUserModal }"
        :style="{ display: showUserModal ? 'block' : 'none' }"
        tabindex="-1"
        role="dialog"
        @click.self="closeUserModal"
      >
        <div class="modal-dialog modal-lg user-modal-dialog" role="document">
          <div class="modal-content user-modal-content">
          <div class="modal-header user-modal-header">
            <h5 class="modal-title">{{ isEditing ? tt("usersList.editUser", "Editar usuario") : tt("usersList.createUser", "Crear usuario") }}</h5>
            <button
              type="button"
              class="close"
              @click="closeUserModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body user-modal-body">
            <form @submit.prevent="handleSubmit" class="user-modal-form">
              <!-- Sección: Información personal -->
              <div class="user-modal-section">
                <div class="user-modal-section-header">
                  <i class="ti-user"></i>
                  <span>{{ tt("usersList.personalInfo", "Información personal") }}</span>
                </div>
                <div class="user-modal-section-content">
                  <div class="row">
                    <div class="col-md-12">
                      <div :class="{ 'has-error': validation.username === 'invalid', 'has-success': validation.username === 'valid' }">
                        <fg-input
                          type="text"
                          :label="tt('usersList.nameRequired', 'Nombre*')"
                          :placeholder="tt('usersList.usernamePlaceholder', 'Nombre de usuario')"
                          v-model="formData.username"
                          @blur="validateField('username')"
                          @input="validateField('username')"
                        >
                          <span v-if="validation.username === 'invalid'" slot="addonRight" class="form-control-feedback">
                            <i class="ti-alert"></i>
                          </span>
                        </fg-input>
                      </div>
                    </div>
                  </div>

                  <div class="row" v-if="!isEditing">
                    <div class="col-md-12">
                      <div :class="{ 'has-error': validation.email === 'invalid', 'has-success': validation.email === 'valid' }">
                        <fg-input
                          type="email"
                          label="Email*"
                          :placeholder="tt('usersList.emailPlaceholder', 'correo@ejemplo.com')"
                          v-model="formData.email"
                          @blur="validateField('email')"
                          @input="validateField('email')"
                        >
                          <span v-if="validation.email === 'invalid'" slot="addonRight" class="form-control-feedback">
                            <i class="ti-alert"></i>
                          </span>
                        </fg-input>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-12">
                      <div :class="{ 'has-error': validation.avatarUrl === 'invalid', 'has-success': validation.avatarUrl === 'valid' }">
                        <fg-input
                          type="text"
                          :label="tt('usersList.avatarUrl', 'URL del avatar')"
                          placeholder="https://example.com/avatar.jpg"
                          v-model="formData.avatarUrl"
                          @blur="validateField('avatarUrl')"
                          @input="validateField('avatarUrl')"
                        >
                          <span v-if="validation.avatarUrl === 'invalid'" slot="addonRight" class="form-control-feedback">
                            <i class="ti-alert"></i>
                          </span>
                        </fg-input>
                        <small v-if="validation.avatarUrl === 'invalid'" class="text-danger user-modal-field-hint">
                          {{ validationErrors.avatarUrl }}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group" :class="{ 'has-error': validation.timezone === 'invalid', 'has-success': validation.timezone === 'valid' }">
                        <label for="timezone" class="control-label">{{ tt("usersList.timezoneRequired", "Zona horaria*") }}</label>
                        <select
                          id="timezone"
                          v-model="formData.timezone"
                          @blur="validateField('timezone')"
                          @change="validateField('timezone')"
                          class="form-control border-input"
                        >
                          <option value="">{{ tt("usersList.selectTimezone", "Seleccionar zona horaria") }}</option>
                          <option v-for="tz in timezones" :key="tz" :value="tz">
                            {{ tz }}
                          </option>
                        </select>
                        <span v-if="validation.timezone === 'invalid'" class="form-control-feedback">
                          <i class="ti-alert"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sección: Información de la cuenta -->
              <div class="user-modal-section">
                <div class="user-modal-section-header">
                  <i class="ti-settings"></i>
                  <span>{{ tt("usersList.accountInfo", "Información de la cuenta") }}</span>
                </div>
                <div class="user-modal-section-content">
                  <template v-if="isEditing">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="showTimelinePostadmin" class="control-label">{{ tt("usersList.showInPostAdminTimeline", "Mostrar en línea de tiempo en post admin") }}</label>
                          <select
                            id="showTimelinePostadmin"
                            v-model="formData.showTimelinePostadmin"
                            class="form-control border-input"
                          >
                            <option value="yes">{{ tt("common.yes", "Sí") }}</option>
                            <option value="no">{{ tt("common.no", "No") }}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label for="hasWatermark" class="control-label">{{ tt("usersList.hasWatermark", "Marca de agua en clips") }}</label>
                          <select
                            id="hasWatermark"
                            v-model="formData.hasWatermark"
                            class="form-control border-input"
                          >
                            <option value="si">{{ tt("common.yes", "Sí") }}</option>
                            <option value="no">{{ tt("common.no", "No") }}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <div v-if="errorMessage" class="alert alert-danger user-modal-error">
                {{ errorMessage }}
              </div>
            </form>
          </div>
          <div class="modal-footer user-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeUserModal"
            >
              {{ tt("usersList.cancel", "Cancelar") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleSubmit"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? tt("usersList.saving", "Guardando...") : (isEditing ? tt("usersList.update", "Actualizar") : tt("usersList.create", "Crear")) }}
            </button>
          </div>
          </div>
        </div>
      </div>
      <div
        v-if="showUserModal"
        class="modal-backdrop fade show"
        @click="closeUserModal"
      ></div>
    </body-portal>

    <!-- Onboarding Q&A Modal -->
    <body-portal>
      <div
        class="modal fade"
        :class="{ show: showOnboardingQaModal }"
        :style="{ display: showOnboardingQaModal ? 'block' : 'none' }"
        tabindex="-1"
        role="dialog"
        @click.self="closeOnboardingQaModal"
      >
        <div class="modal-dialog modal-lg onboarding-qa-modal-dialog" role="document">
          <div class="modal-content onboarding-qa-modal-content">
            <div class="modal-header onboarding-qa-modal-header">
              <h5 class="modal-title">{{ tt("usersList.onboardingQaTitle", "Pregunta y respuesta") }}</h5>
              <button type="button" class="close" @click="closeOnboardingQaModal">
                <span>&times;</span>
              </button>
            </div>
            <div class="modal-body onboarding-qa-modal-body">
              <p v-if="onboardingQaUser" class="onboarding-qa-user-line">
                {{ tt("usersList.targetUser", "Usuario") }}: <strong>{{ onboardingQaUser.username }}</strong>
              </p>
              <div class="onboarding-qa-block">
                <div class="onboarding-qa-question">{{ tt("onboarding.ageRangeLabel", "What is your age range?") }}</div>
                <div class="onboarding-qa-answer">{{ onboardingOptionLabel("ageRangeOptions", onboardingQaUser && onboardingQaUser.user_age_range) }}</div>
              </div>
              <div class="onboarding-qa-block">
                <div class="onboarding-qa-question">{{ tt("onboarding.editingProcessLabel", "How do you describe your current editing process?") }}</div>
                <div class="onboarding-qa-answer">{{ onboardingOptionLabel("editingProcessOptions", onboardingQaUser && onboardingQaUser.user_editing_process) }}</div>
              </div>
              <div class="onboarding-qa-block">
                <div class="onboarding-qa-question">{{ tt("onboarding.socialFrequencyLabel", "What is your current posting frequency on social media?") }}</div>
                <div class="onboarding-qa-answer">{{ onboardingOptionLabel("socialFrequencyOptions", onboardingQaUser && onboardingQaUser.user_social_frequency) }}</div>
              </div>
              <div class="onboarding-qa-block">
                <div class="onboarding-qa-question">{{ tt("onboarding.primaryGoalLabel", "What do you want most?") }}</div>
                <div class="onboarding-qa-answer">{{ onboardingOptionLabel("primaryGoalOptions", onboardingQaUser && onboardingQaUser.user_primary_goal) }}</div>
              </div>
            </div>
            <div class="modal-footer onboarding-qa-modal-footer">
              <button type="button" class="btn btn-primary" @click="closeOnboardingQaModal">
                {{ tt("common.close", "Close") }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showOnboardingQaModal"
        class="modal-backdrop fade show"
        @click="closeOnboardingQaModal"
      ></div>
    </body-portal>

    <!-- Grant PAYG Tokens Modal -->
    <body-portal>
      <div
        class="modal fade"
        :class="{ show: showGrantTokensModal }"
        :style="{ display: showGrantTokensModal ? 'block' : 'none' }"
        tabindex="-1"
        role="dialog"
        @click.self="closeGrantTokensModal"
      >
        <div class="modal-dialog grant-tokens-modal-dialog" role="document">
          <div class="modal-content grant-tokens-modal-content">
          <div class="modal-header grant-tokens-modal-header">
            <h5 class="modal-title">{{ tt("usersList.grantTokensTitle", "Agregar tokens PAYG") }}</h5>
            <button
              type="button"
              class="close"
              @click="closeGrantTokensModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body grant-tokens-modal-body">
            <p class="grant-tokens-target">
              {{ tt("usersList.targetUser", "Usuario") }}: <strong>{{ grantTokensForm.username || grantTokensForm.userId || "N/A" }}</strong>
            </p>
            <div class="form-group">
              <label for="grantTokensInput" class="control-label">{{ tt("usersList.tokensToAdd", "Tokens PAYG a agregar") }}</label>
              <input
                id="grantTokensInput"
                v-model.number="grantTokensForm.tokens"
                type="number"
                min="1"
                step="1"
                class="form-control border-input"
                :placeholder="tt('usersList.tokensPlaceholder', 'Ej. 200')"
                @keydown.enter.prevent="grantPaygTokens"
              >
            </div>
            <div v-if="grantTokensError" class="alert alert-danger mb-0">
              {{ grantTokensError }}
            </div>
          </div>
          <div class="modal-footer grant-tokens-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeGrantTokensModal"
            >
              {{ tt("usersList.cancel", "Cancelar") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="grantPaygTokens"
              :disabled="isGrantingTokens"
            >
              {{ isGrantingTokens ? tt("usersList.grantingTokens", "Agregando...") : tt("usersList.confirmGrantTokens", "Agregar tokens") }}
            </button>
          </div>
          </div>
        </div>
      </div>
      <div
        v-if="showGrantTokensModal"
        class="modal-backdrop fade show"
        @click="closeGrantTokensModal"
      ></div>
    </body-portal>

    <!-- Delete Confirmation Modal -->
    <body-portal>
      <div
        class="modal fade delete-modal-shell"
        :class="{ show: showDeleteModal }"
        :style="{ display: showDeleteModal ? 'block' : 'none' }"
        tabindex="-1"
        role="dialog"
        @click.self="closeDeleteModal"
      >
        <div class="modal-dialog delete-modal-dialog" role="document">
          <div class="modal-content delete-modal-content">
          <div class="modal-header delete-modal-header">
            <h5 class="modal-title">{{ tt("usersList.confirmDelete", "Confirmar eliminación") }}</h5>
            <button
              type="button"
              class="close"
              @click="closeDeleteModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body delete-modal-body">
            <p>{{ tt("usersList.confirmDeleteUser", "¿Seguro que quieres eliminar al usuario") }} <strong>{{ userToDelete?.username }}</strong>?</p>
            <p class="text-danger">{{ tt("usersList.cannotUndo", "Esta acción no se puede deshacer.") }}</p>
          </div>
          <div class="modal-footer delete-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeDeleteModal"
            >
              {{ tt("usersList.cancel", "Cancelar") }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="handleDelete"
              :disabled="isDeleting"
            >
              {{ isDeleting ? tt("usersList.deleting", "Eliminando...") : tt("common.delete", "Eliminar") }}
            </button>
          </div>
          </div>
        </div>
      </div>
      <div
        v-if="showDeleteModal"
        class="modal-backdrop fade show"
        @click="closeDeleteModal"
      ></div>
    </body-portal>
  </div>
</template>

<script>
import { PaperTable, Card } from "@/components";
import FormGroupInput from "@/components/Inputs/formGroupInput.vue";
import { supabase } from "@/supabase";
import escapeToCloseMixin from "@/mixins/escapeToCloseMixin";
import {
  clearImpersonation,
  getImpersonation,
  startImpersonation,
} from "@/utils/impersonation";
import { useCurrentUserStore } from "@/stores/currentUser";
import { getApiUrl as resolveApiUrl } from "@/api/client";

export default {
  name: "UsersList",
  mixins: [escapeToCloseMixin],
  components: {
    PaperTable,
    Card,
    "fg-input": FormGroupInput,
  },
  data() {
    return {
      users: [],
      usersSearchInput: "",
      planFilter: "all",
      showUsersSearchSuggestions: false,
      isLoading: false,
      showUserModal: false,
      showOnboardingQaModal: false,
      onboardingQaUser: null,
      showGrantTokensModal: false,
      showDeleteModal: false,
      isEditing: false,
      isSubmitting: false,
      isGrantingTokens: false,
      isDeleting: false,
      userToDelete: null,
      errorMessage: "",
      grantTokensError: "",
      isAdmin: false,
      isTwitchUser: false,
      impersonation: getImpersonation(),
      grantTokensForm: {
        userId: "",
        userInfoId: null,
        username: "",
        tokens: null,
      },
      tableColumns: ["ID", "Username", "Phone", "Age", "Email", "Origin", "Plan", "Admin", "Timezone", "Created At", "Actions"],
      formData: {
        id: null,
        username: "",
        email: "",
        avatarUrl: "",
        timezone: "",
        showTimelinePostadmin: "no",
        hasWatermark: "no",
      },
      validation: {
        username: null,
        email: null,
        avatarUrl: null,
        timezone: null,
      },
      validationErrors: {
        avatarUrl: "",
      },
      timezones: [],
    };
  },
  async mounted() {
    await this.checkAdminAccess();
    if (this.isAdmin) {
      this.loadTimezones();
      this.fetchUsers();
    }
  },
  computed: {
    filteredUsers() {
      let result = this.users;

      if (this.planFilter === "free") {
        result = result.filter((user) => this.isUserOnFreePlan(user));
      } else if (this.planFilter === "non-free") {
        result = result.filter((user) => !this.isUserOnFreePlan(user));
      }

      const query = this.normalizeUsersSearch(this.usersSearchInput);
      if (!query) return result;
      return result.filter((user) => this.getUserSearchText(user).includes(query));
    },
    usersSearchSuggestions() {
      const query = this.normalizeUsersSearch(this.usersSearchInput);
      if (!query) return [];
      return this.filteredUsers.slice(0, 8);
    },
    escapeToCloseModals() {
      return [
        { show: "showUserModal", close: "closeUserModal" },
        { show: "showOnboardingQaModal", close: "closeOnboardingQaModal" },
        { show: "showGrantTokensModal", close: "closeGrantTokensModal" },
        { show: "showDeleteModal", close: "closeDeleteModal" },
      ];
    },
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    normalizeUsersSearch(value) {
      return String(value || "").trim().toLowerCase();
    },
    isUserOnFreePlan(user) {
      const label = String(user?.plan_label || "Free").trim();
      return !label || label === "Free";
    },
    getUserSearchText(user) {
      return [
        user?.username,
        this.getUserEmail(user),
        this.getSignupOriginLabel(user),
        user?.signup_utm_campaign,
        user?.signup_utm_source,
      ]
        .map((value) => this.normalizeUsersSearch(value))
        .filter(Boolean)
        .join(" ");
    },
    getUserSearchInitial(user) {
      const text = String(user?.username || this.getUserEmail(user) || "").trim();
      return text ? text.charAt(0).toUpperCase() : "?";
    },
    selectUsersSearchSuggestion(user) {
      const username = String(user?.username || "").trim();
      const email = this.getUserEmail(user);
      this.usersSearchInput = username || email;
      this.showUsersSearchSuggestions = false;
    },
    hideUsersSearchSuggestions() {
      this.showUsersSearchSuggestions = false;
    },
    async checkAdminAccess() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          this.$router.push("/login");
          return;
        }

        const currentUserStore = useCurrentUserStore();
        await currentUserStore.ensureSessionReady(session);
        if (currentUserStore.isImpersonating) {
          this.isAdmin = false;
          this.$router.push("/create-clips");
          return;
        }

        if (!currentUserStore.hasAdminAccess) {
          // Redirect to Clipr base route if not admin
          this.$router.push("/create-clips");
          return;
        }

        this.isAdmin = true;
        this.isTwitchUser = currentUserStore.isTwitchUser;
      } catch (error) {
        console.error("Error checking admin access:", error);
        this.$router.push("/create-clips");
      }
    },
    loadTimezones() {
      this.timezones = this.getAllTimezones();
    },
    getAllTimezones() {
      return [
        'EST',
        'PST',
        'CST',
        'MST',
        'UTC'
      ];
    },
    async fetchUsers() {
      this.isLoading = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const apiUrl = resolveApiUrl();
        const headers = {
          "Content-Type": "application/json",
        };

        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }

        const response = await fetch(`${apiUrl}/momo/get-users`, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          throw new Error(`${this.tt("usersList.loadUsersError", "No se pudieron cargar los usuarios. Inténtalo de nuevo.")}: ${response.statusText}`);
        }

        const data = await response.json();
        const rawUsers = data.users || data || [];
        this.users = this.filterUsersByContext(rawUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
        this.errorMessage = this.tt("usersList.loadUsersError", "No se pudieron cargar los usuarios. Inténtalo de nuevo.");
        this.users = [];
      } finally {
        this.isLoading = false;
      }
    },
    filterUsersByContext(rawUsers) {
      if (!Array.isArray(rawUsers)) return [];
      if (this.isTwitchUser) {
        return rawUsers.filter((user) => user && user.twitch_user === true);
      }
      return rawUsers;
    },
    canImpersonateUsers() {
      return this.isTwitchUser;
    },
    isImpersonatingRow(row) {
      const active = this.impersonation;
      if (!active || !row) return false;
      return String(active.userInfoId) === String(row.id);
    },
    getUserEmail(row) {
      if (!row || typeof row !== "object") return "";
      const userInfo = Array.isArray(row.user_info) ? row.user_info[0] : row.user_info;
      if (userInfo && typeof userInfo === "object" && userInfo.user_email) {
        return userInfo.user_email;
      }
      return row.user_email || row.email || "";
    },
    getUserPhone(row) {
      if (!row || typeof row !== "object") return "";
      const userInfo = Array.isArray(row.user_info) ? row.user_info[0] : row.user_info;
      if (userInfo && typeof userInfo === "object" && userInfo.user_phone) {
        return userInfo.user_phone;
      }
      return row.user_phone || row.phone || "";
    },
    getSignupOriginLabel(row) {
      const origin = String(row?.signup_origin || "").trim();
      const campaign = String(row?.signup_utm_campaign || "").trim();
      if (campaign) return campaign;
      if (origin === "facebook_ads") return this.tt("usersList.originFacebookAds", "Facebook Ads");
      if (origin === "ad_campaign") return this.tt("usersList.originAdCampaign", "Campaña");
      if (origin === "admin_generated") return this.tt("usersList.originAdminGenerated", "Admin");
      if (origin === "organic") return this.tt("usersList.originOrganic", "Orgánico");
      return this.tt("usersList.originUnknownExisting", "Histórico");
    },
    getSignupOriginTitle(row) {
      const parts = [
        this.getSignupOriginLabel(row),
        row?.signup_auth_method ? `auth=${row.signup_auth_method}` : "",
        row?.signup_utm_source ? `utm_source=${row.signup_utm_source}` : "",
        row?.signup_utm_medium ? `utm_medium=${row.signup_utm_medium}` : "",
        row?.signup_utm_campaign ? `utm_campaign=${row.signup_utm_campaign}` : "",
        row?.signup_utm_content ? `utm_content=${row.signup_utm_content}` : "",
        row?.signup_utm_term ? `utm_term=${row.signup_utm_term}` : "",
        row?.signup_source_url ? `url=${row.signup_source_url}` : "",
      ].filter(Boolean);
      return parts.join(" | ");
    },
    getSignupOriginClass(row) {
      const origin = String(row?.signup_origin || "").trim();
      return {
        "users-origin-badge--ad": origin === "facebook_ads" || origin === "ad_campaign",
        "users-origin-badge--admin": origin === "admin_generated",
        "users-origin-badge--organic": origin === "organic",
      };
    },
    resolveTargetUserId(row) {
      if (!row || typeof row !== "object") return "";
      return row.user_id || row.auth_user_id || row.authUserId || row.uuid || "";
    },
    async resolveAuthUserIdFromUserInfoId(userInfoId) {
      if (userInfoId == null) return null;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error(this.tt("usersList.sessionRequired", "Debes iniciar sesión."));
      }

      const apiUrl = resolveApiUrl();
      const response = await fetch(
        `${apiUrl}/momo/twitch/user-info/user-id?user_info_id=${encodeURIComponent(userInfoId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errData.detail || this.tt("usersList.userIdResolveError", "No se pudo resolver el user_id del usuario."));
      }

      const payload = await response.json();
      return payload?.user_id || null;
    },
    async openGrantTokensModal(user) {
      let targetUserId = this.resolveTargetUserId(user);
      if (!targetUserId && user?.id != null) {
        try {
          targetUserId = await this.resolveAuthUserIdFromUserInfoId(user.id);
        } catch (error) {
          console.error("Error resolving user_id from user_info id:", error);
          this.$notify({
            message: error.message || this.tt("usersList.userIdResolveError", "No se pudo resolver el user_id del usuario."),
            type: "danger",
          });
          return;
        }
      }

      if (!targetUserId) {
        this.$notify({
          message: this.tt("usersList.userIdMissing", "No se encontró user_id para este usuario."),
          type: "danger",
        });
        return;
      }

      this.grantTokensError = "";
      this.grantTokensForm = {
        userId: targetUserId,
        userInfoId: user?.id || null,
        username: user?.username || "",
        tokens: null,
      };
      this.showGrantTokensModal = true;
      document.body.style.overflow = "hidden";
    },
    openOnboardingQaModal(user) {
      this.onboardingQaUser = user || null;
      this.showOnboardingQaModal = true;
      document.body.style.overflow = "hidden";
    },
    closeOnboardingQaModal() {
      this.showOnboardingQaModal = false;
      this.onboardingQaUser = null;
      document.body.style.overflow = "";
    },
    onboardingOptionLabel(optionsGroup, value) {
      if (value == null || String(value).trim() === "") {
        return this.tt("usersList.onboardingAnswerNotSet", "—");
      }
      const optionKey = optionsGroup === "ageRangeOptions"
        ? String(value).replace("-", "_").replace("+", "_plus")
        : String(value);
      const key = `onboarding.${optionsGroup}.${optionKey}`;
      return this.tt(key, String(value));
    },
    closeGrantTokensModal() {
      this.showGrantTokensModal = false;
      this.grantTokensError = "";
      this.grantTokensForm = {
        userId: "",
        userInfoId: null,
        username: "",
        tokens: null,
      };
      document.body.style.overflow = "";
    },
    async grantPaygTokens() {
      const normalizedTokens = Number(this.grantTokensForm.tokens);
      if (!Number.isInteger(normalizedTokens) || normalizedTokens <= 0) {
        this.grantTokensError = this.tt("usersList.invalidTokens", "Ingresa una cantidad valida de tokens (entero mayor a 0).");
        return;
      }
      if (!this.grantTokensForm.userId) {
        this.grantTokensError = this.tt("usersList.userIdMissing", "No se encontró user_id para este usuario.");
        return;
      }

      this.isGrantingTokens = true;
      this.grantTokensError = "";

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const apiUrl = resolveApiUrl();
        const headers = {
          "Content-Type": "application/json",
        };

        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }

        const payload = {
          user_id: this.grantTokensForm.userId,
          tokens: normalizedTokens,
          pack_code: "admin_manual",
          currency: "USD",
          amount: 0,
        };

        const response = await fetch(`${apiUrl}/momo/get-users/admin/grant-payg-tokens`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          const backendMessage = result?.detail || result?.message || response.statusText;
          throw new Error(backendMessage || this.tt("usersList.grantTokensError", "No se pudieron agregar tokens PAYG"));
        }

        const added = Number(result?.tokens_added);
        const remaining = Number(result?.credits_remaining);
        const addedLabel = Number.isFinite(added) ? added : normalizedTokens;
        const remainingLabel = Number.isFinite(remaining) ? ` | ${this.tt("usersList.balance", "Saldo")}: ${remaining}` : "";

        this.$notify({
          message: `${this.tt("usersList.tokensGranted", "Tokens agregados correctamente")}: ${addedLabel}${remainingLabel}`,
          type: "success",
        });

        await this.fetchUsers();
        try {
          await useCurrentUserStore().refresh();
        } catch (refreshError) {
          console.error("Error refreshing current user after granting tokens:", refreshError);
        }
        this.closeGrantTokensModal();
      } catch (error) {
        console.error("Error granting PAYG tokens:", error);
        this.grantTokensError = error.message || this.tt("usersList.grantTokensRetryError", "No se pudieron agregar tokens. Inténtalo de nuevo.");
        this.$notify({
          message: this.grantTokensError,
          type: "danger",
        });
      } finally {
        this.isGrantingTokens = false;
      }
    },
    async impersonateUser(user) {
      if (!user || !user.id) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          this.$router.push("/login");
          return;
        }

        const apiUrl = resolveApiUrl();
        const failMsg = this.tt("usersList.impersonationFailed", "No se pudo iniciar suplantación de usuario");
        await startImpersonation({
          session,
          userInfoId: user.id,
          username: user.username || "",
          email: user.email || "",
          apiUrl,
          impersonationFailedMessage: failMsg,
        });
        await useCurrentUserStore().refresh();
        this.impersonation = getImpersonation();

        this.$notify({
          message: this.tt("usersList.impersonationStarted", "Suplantación iniciada."),
          type: "success",
        });

        this.$router.push("/create-clips");
      } catch (error) {
        console.error("Error impersonating user:", error);
        this.$notify({
          message: error.message || this.tt("usersList.impersonationFailed", "No se pudo iniciar suplantación de usuario"),
          type: "danger",
        });
      }
    },
    async stopImpersonation() {
      clearImpersonation();
      this.impersonation = null;
      await useCurrentUserStore().refresh();
      this.$notify({
        message: this.tt("usersList.impersonationEnded", "Suplantación finalizada."),
        type: "info",
      });
      this.$router.push("/users");
    },
    openCreateModal() {
      this.isEditing = false;
      this.formData = {
        id: null,
        username: "",
        email: "",
        avatarUrl: "",
        timezone: "",
        showTimelinePostadmin: "no",
        hasWatermark: "no",
      };
      this.validation = {
        username: null,
        email: null,
        avatarUrl: null,
        timezone: null,
      };
      this.errorMessage = "";
      this.showUserModal = true;
      document.body.style.overflow = "hidden";
    },
    openEditModal(user) {
      this.isEditing = true;
      const timezone = user.timezone || "";
      const hasWatermarkValue = user.has_watermark;
      const hasWatermarkEnabled =
        hasWatermarkValue === true ||
        hasWatermarkValue === 1 ||
        hasWatermarkValue === "1" ||
        (typeof hasWatermarkValue === "string" && hasWatermarkValue.toLowerCase() === "true");
      if (timezone && !this.timezones.includes(timezone)) {
        this.timezones = [...this.timezones, timezone];
      }
      this.formData = {
        id: user.id,
        username: user.username || "",
        email: user.email || "",
        avatarUrl: user.avatar_url || user.avatarUrl || "",
        timezone,
        showTimelinePostadmin: user.show_timeline_user_postadmin === true ? "yes" : "no",
        hasWatermark: hasWatermarkEnabled ? "si" : "no",
      };
      this.validation = {
        username: null,
        email: null,
        avatarUrl: null,
        timezone: null,
      };
      this.errorMessage = "";
      this.showUserModal = true;
      document.body.style.overflow = "hidden";
    },
    closeUserModal() {
      this.showUserModal = false;
      document.body.style.overflow = "";
      this.errorMessage = "";
    },
    validateField(fieldName) {
      const value = this.formData[fieldName];
      
      if (fieldName === 'avatarUrl') {
        if (value && value.trim() !== '') {
          const urlPattern = /^https?:\/\/.+/i;
          if (!urlPattern.test(value)) {
            this.validation.avatarUrl = 'invalid';
            this.validationErrors.avatarUrl = this.tt("usersList.avatarUrlInvalid", "La URL debe comenzar con http:// o https://");
          } else {
            this.validation.avatarUrl = 'valid';
            this.validationErrors.avatarUrl = '';
          }
        } else {
          this.validation.avatarUrl = null;
          this.validationErrors.avatarUrl = '';
        }
      } else if (fieldName === 'email' && !this.isEditing) {
        if (!value || value.trim() === '') {
          this.validation.email = 'invalid';
        } else {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(value)) {
            this.validation.email = 'invalid';
          } else {
            this.validation.email = 'valid';
          }
        }
      } else {
        if (!value || value.trim() === '') {
          this.validation[fieldName] = 'invalid';
        } else {
          this.validation[fieldName] = 'valid';
        }
      }
    },
    validateAllFields() {
      this.validateField('username');
      if (!this.isEditing) {
        this.validateField('email');
      }
      this.validateField('avatarUrl');
      this.validateField('timezone');
    },
    isFormValid() {
      const baseValid = (
        this.validation.username === 'valid' &&
        this.validation.timezone === 'valid'
      );
      
      if (this.isEditing) {
        return baseValid && (this.validation.avatarUrl === null || this.validation.avatarUrl === 'valid');
      } else {
        return baseValid && 
               this.validation.email === 'valid' &&
               (this.validation.avatarUrl === null || this.validation.avatarUrl === 'valid');
      }
    },
    async handleSubmit() {
      this.validateAllFields();
      
      if (!this.isFormValid()) {
        this.errorMessage = this.tt("usersList.requiredFieldsError", "Completa correctamente todos los campos obligatorios");
        return;
      }
      
      this.isSubmitting = true;
      this.errorMessage = "";
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const apiUrl = resolveApiUrl();
        const headers = {
          "Content-Type": "application/json",
        };
        
        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }
        
        const payload = {
          username: this.formData.username,
          timezone: this.formData.timezone,
        };
        
        if (this.formData.avatarUrl) {
          payload.avatar_url = this.formData.avatarUrl;
        }
        
        if (!this.isEditing) {
          payload.email = this.formData.email;
          payload.twitch_user = true;
        } else {
          payload.show_timeline_user_postadmin = this.formData.showTimelinePostadmin === "yes";
          payload.has_watermark = this.formData.hasWatermark === "si";
        }
        
        const url = this.isEditing
          ? `${apiUrl}/momo/users/${this.formData.id}`
          : `${apiUrl}/momo/users/`;
        
        const method = this.isEditing ? "PUT" : "POST";
        
        const response = await fetch(url, {
          method: method,
          headers: headers,
          body: JSON.stringify(payload),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: response.statusText }));
          const backendMessage = errorData.detail || errorData.message || "";

          if (
            !this.isEditing &&
            typeof backendMessage === "string" &&
            backendMessage.toLowerCase().includes("already been registered")
          ) {
            throw new Error("Ya existe un usuario con ese correo.");
          }

          throw new Error(
            backendMessage ||
              (this.isEditing
                ? this.tt("usersList.updateUserError", "No se pudo actualizar el usuario")
                : this.tt("usersList.createUserError", "No se pudo crear el usuario"))
          );
        }
        
        await this.fetchUsers();
        try {
          await useCurrentUserStore().refresh();
        } catch (refreshError) {
          console.error("Error refreshing current user after user save:", refreshError);
        }
        this.closeUserModal();
      } catch (error) {
        console.error(`Error ${this.isEditing ? 'updating' : 'creating'} user:`, error);
        this.errorMessage =
          error.message ||
          (this.isEditing
            ? this.tt("usersList.updateUserRetryError", "No se pudo actualizar el usuario. Inténtalo de nuevo.")
            : this.tt("usersList.createUserRetryError", "No se pudo crear el usuario. Inténtalo de nuevo."));
      } finally {
        this.isSubmitting = false;
      }
    },
    confirmDelete(user) {
      this.userToDelete = user;
      this.showDeleteModal = true;
      document.body.style.overflow = "hidden";
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.userToDelete = null;
      document.body.style.overflow = "";
    },
    async handleDelete() {
      if (!this.userToDelete) return;
      
      this.isDeleting = true;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const apiUrl = resolveApiUrl();
        const headers = {
          "Content-Type": "application/json",
        };
        
        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }
        
        const response = await fetch(`${apiUrl}/momo/users/${this.userToDelete.id}`, {
          method: "DELETE",
          headers: headers,
        });
        
        if (!response.ok) {
          throw new Error(`${this.tt("usersList.deleteUserError", "No se pudo eliminar el usuario")}: ${response.statusText}`);
        }
        
        await this.fetchUsers();
        this.closeDeleteModal();
      } catch (error) {
        console.error("Error deleting user:", error);
        this.errorMessage = error.message || this.tt("usersList.deleteUserRetryError", "No se pudo eliminar el usuario. Inténtalo de nuevo.");
        alert(this.errorMessage);
      } finally {
        this.isDeleting = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return dateString;
      }
    },
    exportUsersCsv() {
      if (this.isLoading || !this.users.length) return;

      const escapeCsvValue = (value) => {
        const normalized = String(value == null ? "" : value);
        return `"${normalized.replace(/"/g, '""')}"`;
      };

      const header = ["ID", "Usuario", "Teléfono", "Edad", "Email", "Origen", "Método", "UTM Source", "UTM Medium", "UTM Campaign"];
      const rows = this.users.map((row) => [
        escapeCsvValue(row?.id),
        escapeCsvValue(row?.username),
        escapeCsvValue(this.getUserPhone(row)),
        escapeCsvValue(row?.user_age_range),
        escapeCsvValue(this.getUserEmail(row)),
        escapeCsvValue(this.getSignupOriginLabel(row)),
        escapeCsvValue(row?.signup_auth_method),
        escapeCsvValue(row?.signup_utm_source),
        escapeCsvValue(row?.signup_utm_medium),
        escapeCsvValue(row?.signup_utm_campaign),
      ]);
      const csvContent = ["\uFEFF" + header.join(","), ...rows.map((row) => row.join(","))].join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    },
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
.modal.show {
  opacity: 1;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.5;
}

/* ===== Modales de UsersList (body-portal) ===== */
.user-modal-dialog,
.onboarding-qa-modal-dialog,
.grant-tokens-modal-dialog,
.delete-modal-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.user-modal-content,
.onboarding-qa-modal-content,
.grant-tokens-modal-content,
.delete-modal-content {
  border-radius: 16px;
  border: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-card);
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.85), 0 0 50px var(--clipr-purple-glow);
  color: var(--clipr-text-primary);
}

.user-modal-header,
.onboarding-qa-modal-header,
.grant-tokens-modal-header,
.delete-modal-header {
  padding: 1.25rem 1.75rem;
  border-bottom: 1px solid var(--clipr-border-color);
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(217, 70, 239, 0.08) 100%);
  color: var(--clipr-text-primary);
}

.user-modal-header .modal-title,
.onboarding-qa-modal-header .modal-title,
.grant-tokens-modal-header .modal-title,
.delete-modal-header .modal-title {
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.user-modal-header .modal-title {
  font-size: 1.25rem;
}

.user-modal-header .close,
.onboarding-qa-modal-header .close,
.grant-tokens-modal-header .close,
.delete-modal-header .close {
  color: var(--clipr-text-secondary);
  opacity: 1;
  text-shadow: none;
}

.user-modal-header .close:hover,
.onboarding-qa-modal-header .close:hover,
.grant-tokens-modal-header .close:hover,
.delete-modal-header .close:hover {
  color: var(--clipr-text-primary);
}

.user-modal-body {
  padding: 1.5rem 1.75rem;
  max-height: 75vh;
  overflow-y: auto;
  background: var(--clipr-bg-card);
  color: var(--clipr-text-primary);
}

.onboarding-qa-modal-body,
.grant-tokens-modal-body,
.delete-modal-body {
  background: var(--clipr-bg-card);
  color: var(--clipr-text-primary);
}

.user-modal-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.user-modal-section {
  background: var(--clipr-bg-elevated);
  border-radius: 12px;
  border: 1px solid var(--clipr-border-color);
  overflow: hidden;
}

.user-modal-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.16) 0%, rgba(217, 70, 239, 0.06) 100%);
  color: var(--clipr-purple-light);
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.02em;
  border-bottom: 1px solid var(--clipr-border-color);
}

.user-modal-section-header i {
  font-size: 1rem;
  opacity: 0.85;
  color: var(--clipr-purple-light);
}

.user-modal-section-content {
  padding: 1.25rem 1rem;
  background: var(--clipr-bg-elevated);
}

.user-modal-section-content .row {
  margin-bottom: 0;
}

.user-modal-section-content .row + .row {
  margin-top: 0.75rem;
}

.user-modal-section-content .col-md-12 + .col-md-12 {
  margin-top: 0.75rem;
}

.user-modal-field-hint {
  display: block;
  margin-top: 0.35rem;
}

.user-modal-error {
  margin-bottom: 0;
  border-radius: 8px;
}

.user-modal-footer {
  padding: 1rem 1.75rem;
  gap: 0.75rem;
  border-top: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-elevated);
}

.onboarding-qa-modal-footer,
.grant-tokens-modal-footer,
.delete-modal-footer {
  border-top: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-elevated);
}

.user-modal-footer .btn-primary,
.onboarding-qa-modal-footer .btn-primary,
.grant-tokens-modal-footer .btn-primary {
  background: var(--clipr-purple-gradient);
  box-shadow: 0 8px 30px var(--clipr-purple-glow);
  border: none;
  color: white;
}

.user-modal-footer .btn-secondary,
.grant-tokens-modal-footer .btn-secondary,
.delete-modal-footer .btn-secondary {
  background: var(--clipr-bg-elevated);
  border: 1px solid var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.delete-modal-footer .btn-danger {
  border: 1px solid rgba(248, 113, 113, 0.35);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%);
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.35);
  color: white;
}

/* Inputs más visibles en el modal (nombre, avatar, etc.) */
.user-modal-body :deep(.form-control),
.user-modal-body :deep(input.form-control),
.user-modal-body :deep(select.form-control),
.user-modal-body :deep(.border-input),
.grant-tokens-modal-body .form-control,
.grant-tokens-modal-body .border-input {
  background: var(--clipr-bg-elevated) !important;
  border: 1px solid var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.user-modal-body :deep(.form-control::placeholder),
.user-modal-body :deep(input.form-control::placeholder),
.user-modal-body :deep(select.form-control::placeholder),
.user-modal-body :deep(.border-input::placeholder),
.grant-tokens-modal-body .form-control::placeholder,
.grant-tokens-modal-body .border-input::placeholder {
  color: var(--clipr-text-tertiary) !important;
}

.user-modal-body :deep(.form-control:focus),
.user-modal-body :deep(input.form-control:focus),
.user-modal-body :deep(select.form-control:focus),
.user-modal-body :deep(.border-input:focus),
.grant-tokens-modal-body .form-control:focus,
.grant-tokens-modal-body .border-input:focus {
  border-color: var(--clipr-border-purple) !important;
  box-shadow: 0 0 0 0.2rem var(--clipr-purple-glow) !important;
}

.user-modal-body :deep(.control-label),
.grant-tokens-modal-body .control-label {
  color: var(--clipr-text-secondary) !important;
}

.delete-modal-body p {
  color: var(--clipr-text-primary);
}

.delete-modal-body .text-danger {
  color: #f87171 !important;
}

/* Ocultar flechas/spinners en inputs numéricos - Chrome */
.user-modal-body :deep(input[type="number"])::-webkit-inner-spin-button,
.user-modal-body :deep(input[type="number"])::-webkit-outer-spin-button {
  -webkit-appearance: none !important;
  appearance: none !important;
  margin: 0;
  opacity: 0;
  pointer-events: none;
  height: 0;
  width: 0;
}

.user-modal-body :deep(input[type="number"]) {
  -moz-appearance: textfield !important;
  appearance: textfield !important;
}

:deep(.form-control-feedback) {
  right: 30px !important;
  top: 55% !important;
  margin-top: 0 !important;
}

.form-group {
  position: relative;
}

.form-group select.form-control {
  padding-right: 50px;
}

:deep(.input-group) {
  .form-control {
    padding-right: 50px;
  }
}

.ti-spin {
  animation: spin 1s infinite linear;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.badge {
  padding: 0.25em 0.6em;
  font-size: 0.875em;
  font-weight: 600;
  border-radius: 0.25rem;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

/* ===== Tabla de usuarios - diseño mejorado ===== */
.users-table-wrapper {
  min-height: 200px;
  position: relative;
}

.users-search-toolbar {
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.users-search-toolbar-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.users-search-input-wrap {
  position: relative;
  width: min(420px, 100%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  color: #64748b;
}

.users-search-input {
  width: 100%;
  min-height: 40px;
  border: none;
  outline: none;
  background: transparent;
  color: #1e293b;
  font-size: 13px;
}

.users-search-input::placeholder {
  color: #94a3b8;
}

.users-plan-filter-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  min-height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
}

.users-plan-filter-label {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.users-plan-filter-select {
  border: none;
  outline: none;
  background: transparent;
  color: #1e293b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding-right: 4px;
}

.users-search-suggestions {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 280px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.18);
}

.users-search-suggestion {
  width: 100%;
  border: none;
  border-radius: 10px;
  padding: 8px;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  cursor: pointer;
}

.users-search-suggestion:hover {
  background: #f1f5f9;
}

.users-search-suggestion-avatar {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: #e0e7ff;
  color: #4f46e5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  flex: 0 0 auto;
}

.users-search-suggestion-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.users-search-suggestion-copy strong {
  color: #1e293b;
  font-size: 13px;
}

.users-search-suggestion-copy small {
  color: #64748b;
  font-size: 11px;
}

.users-table-responsive {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.users-table-wrapper :deep(.users-table.table),
.users-table-wrapper :deep(table.users-table) {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.users-table-wrapper :deep(.users-table thead th) {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  color: #475569;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 8px;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

.users-table-wrapper :deep(.users-table tbody td) {
  padding: 8px 8px;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Anchos fijos por columna */
.users-table-wrapper :deep(.col-id) { width: 50px; }
.users-table-wrapper :deep(.col-username) { width: 140px; }
.users-table-wrapper :deep(.col-phone) { width: 130px; }
.users-table-wrapper :deep(.col-age) { width: 70px; }
.users-table-wrapper :deep(.col-email) { width: 200px; }
.users-table-wrapper :deep(.col-origin) { width: 125px; }
.users-table-wrapper :deep(.col-plan) { width: 80px; }
.users-table-wrapper :deep(.col-admin) { width: 55px; }
.users-table-wrapper :deep(.col-timezone) { width: 90px; }
.users-table-wrapper :deep(.col-created) { width: 110px; }
.users-table-wrapper :deep(.col-actions) { width: 170px; }

.users-table-wrapper :deep(.users-table tbody tr:hover) {
  background-color: #f8fafc;
}

.users-table-wrapper :deep(.users-table tbody tr:last-child td) {
  border-bottom: none;
}

.users-table-username .username-cell {
  font-weight: 600;
  color: #1e293b;
}

.users-table-id {
  color: #64748b;
  font-family: ui-monospace, monospace;
  font-size: 0.85rem;
}

.users-table-email {
  color: #475569;
}

.users-table-phone {
  color: #475569;
}

.users-table-age {
  color: #475569;
}

.users-table-origin {
  color: #475569;
}

.users-origin-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 0.68rem;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.users-origin-badge--ad {
  background: rgba(16, 185, 129, 0.15);
  color: #047857;
}

.users-origin-badge--admin {
  background: rgba(245, 158, 11, 0.18);
  color: #92400e;
}

.users-origin-badge--organic {
  background: rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
}

.users-table-plan {
  overflow: visible;
}

.users-plan-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: #64748b;
  font-weight: 600;
  font-size: 0.68rem;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.users-plan-badge--paid {
  background: #059669;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 0 0 1px rgba(5, 150, 105, 0.35);
}

.users-table-date {
  color: #64748b;
  font-size: 0.85rem;
}

.users-export-button {
  width: 40px;
  height: 40px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  transition: all 0.2s ease;
}

.users-export-button:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #1e293b;
  transform: translateY(-1px);
}

.users-export-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Botones de acción */
.action-buttons {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
}

.btn-action {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
  transition: all 0.2s ease;
}

.btn-action-impersonate {
  background: #e0e7ff;
  color: #4f46e5;
}

.btn-action-impersonate:hover:not(:disabled) {
  background: #4f46e5;
  color: white;
  transform: translateY(-1px);
}

.btn-action-impersonate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-action-tokens {
  background: #d1fae5;
  color: #047857;
}

.btn-action-tokens:hover:not(:disabled) {
  background: #10b981;
  color: white;
  transform: translateY(-1px);
}

.btn-action-tokens:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-action-qa {
  background: #e0e7ff;
  color: #4338ca;
}

.btn-action-qa:hover {
  background: #6366f1;
  color: white;
  transform: translateY(-1px);
}

.btn-action-edit {
  background: #fef08a;
  color: #854d0e;
}

.btn-action-edit:hover {
  background: #eab308;
  color: white;
  transform: translateY(-1px);
}

.btn-action-delete {
  background: #fee2e2;
  color: #dc2626;
}

.btn-action-delete:hover {
  background: #dc2626;
  color: white;
  transform: translateY(-1px);
}

/* Estados loading y empty */
.users-table-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.users-table-loading i,
.users-table-empty i {
  font-size: 2.5rem;
  color: #94a3b8;
  margin-bottom: 12px;
}

.users-table-state p {
  margin: 0;
  color: #64748b;
  font-size: 1rem;
}

.users-table-empty-hint {
  margin-top: 8px;
  font-size: 0.875rem;
  color: #94a3b8;
}

.onboarding-qa-modal-body {
  padding-top: 0.8rem;
}

.onboarding-qa-user-line {
  margin-bottom: 1rem;
  color: var(--clipr-text-secondary);
}

.onboarding-qa-block {
  margin-bottom: 1.25rem;
  padding: 0.95rem 1rem;
  border: 1px solid var(--clipr-border-color);
  border-radius: 10px;
  background: var(--clipr-bg-elevated);
}

.onboarding-qa-block:last-child {
  margin-bottom: 0;
}

.onboarding-qa-question {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--clipr-text-tertiary);
  margin-bottom: 0.35rem;
}

.onboarding-qa-answer {
  font-size: 1rem;
  color: var(--clipr-text-primary);
  line-height: 1.45;
}

/* Estilos Twitch - tema oscuro con acentos púrpura */
.users-list-page.twitch-theme :deep(.card) {
  background: var(--clipr-bg-card) !important;
  border: 1px solid var(--clipr-border-color) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5), 0 0 30px var(--clipr-purple-glow) !important;
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.card-header),
.users-list-page.twitch-theme :deep(.card-title) {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.card-body) {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.btn-primary) {
  background: var(--clipr-purple-gradient) !important;
  box-shadow: 0 8px 30px var(--clipr-purple-glow) !important;
  border: none !important;
  color: white !important;
}

.users-list-page.twitch-theme :deep(.btn-primary:hover) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%) !important;
  box-shadow: 0 12px 40px rgba(168, 85, 247, 0.5) !important;
  transform: translateY(-2px);
}

.users-list-page.twitch-theme .users-export-button {
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35) !important;
}

.users-list-page.twitch-theme .users-export-button:hover:not(:disabled) {
  background: rgba(168, 85, 247, 0.15) !important;
  border-color: var(--clipr-border-purple) !important;
  color: var(--clipr-purple-light) !important;
}

.users-list-page.twitch-theme .users-search-toolbar {
  background: var(--clipr-bg-card) !important;
  border-bottom-color: var(--clipr-border-color) !important;
}

.users-list-page.twitch-theme .users-search-input-wrap {
  background: rgba(15, 23, 42, 0.35) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-tertiary) !important;
}

.users-list-page.twitch-theme .users-search-input {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme .users-search-input::placeholder {
  color: var(--clipr-text-tertiary) !important;
}

.users-list-page.twitch-theme .users-search-suggestions {
  background: var(--clipr-bg-card) !important;
  border-color: rgba(168, 85, 247, 0.28) !important;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.34) !important;
}

.users-list-page.twitch-theme .users-search-suggestion:hover {
  background: rgba(168, 85, 247, 0.14) !important;
}

.users-list-page.twitch-theme .users-search-suggestion-avatar {
  background: rgba(168, 85, 247, 0.2) !important;
  color: var(--clipr-purple-light) !important;
}

.users-list-page.twitch-theme .users-search-suggestion-copy strong {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme .users-search-suggestion-copy small {
  color: var(--clipr-text-tertiary) !important;
}

.users-list-page.twitch-theme :deep(.btn-info) {
  background: var(--clipr-purple-gradient) !important;
  box-shadow: 0 8px 30px var(--clipr-purple-glow) !important;
  border: none !important;
  color: white !important;
}

.users-list-page.twitch-theme :deep(.btn-info:hover) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%) !important;
  box-shadow: 0 12px 40px rgba(168, 85, 247, 0.5) !important;
  transform: translateY(-2px);
}

.users-list-page.twitch-theme .text-muted {
  color: var(--clipr-text-secondary) !important;
}

.users-list-page.twitch-theme :deep(.badge-info) {
  background: var(--clipr-purple-gradient) !important;
  box-shadow: 0 2px 8px var(--clipr-purple-glow) !important;
  color: white !important;
}

.users-list-page.twitch-theme :deep(.badge-warning) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%) !important;
  color: white !important;
  border: none !important;
}

/* Tabla con tema Twitch */
.users-list-page.twitch-theme .users-table-responsive {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5), 0 0 20px var(--clipr-purple-glow) !important;
  border: 1px solid var(--clipr-border-color) !important;
}

.users-list-page.twitch-theme :deep(.table),
.users-list-page.twitch-theme :deep(table) {
  color: var(--clipr-text-primary) !important;
  background: var(--clipr-bg-card) !important;
  border-collapse: separate;
  border-spacing: 0;
}

.users-list-page.twitch-theme :deep(.users-table thead th) {
  background: rgba(168, 85, 247, 0.15) !important;
  color: var(--clipr-purple-light) !important;
  border-bottom-color: var(--clipr-border-purple) !important;
}

/* Fondo de filas y celdas por defecto */
.users-list-page.twitch-theme :deep(.table tbody tr),
.users-list-page.twitch-theme :deep(table tbody tr) {
  background: var(--clipr-bg-elevated) !important;
}

.users-list-page.twitch-theme :deep(.table td),
.users-list-page.twitch-theme :deep(table td) {
  color: var(--clipr-text-primary) !important;
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
  padding: 8px 8px;
}

.users-list-page.twitch-theme :deep(.table tbody tr:hover),
.users-list-page.twitch-theme :deep(table tbody tr:hover) {
  background: rgba(168, 85, 247, 0.08) !important;
}

.users-list-page.twitch-theme .users-table-username .username-cell {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme .users-table-id,
.users-list-page.twitch-theme .users-table-phone,
.users-list-page.twitch-theme .users-table-age,
.users-list-page.twitch-theme .users-table-email,
.users-list-page.twitch-theme .users-table-plan,
.users-list-page.twitch-theme .users-table-date {
  color: var(--clipr-text-secondary) !important;
}

.users-list-page.twitch-theme .users-table-plan--paid {
  color: #39ff88 !important;
  text-shadow:
    0 0 10px rgba(57, 255, 136, 0.7),
    0 0 20px rgba(57, 255, 136, 0.45);
}

.users-list-page.twitch-theme .btn-action-impersonate {
  background: rgba(168, 85, 247, 0.2) !important;
  color: var(--clipr-purple-light) !important;
}

.users-list-page.twitch-theme .btn-action-impersonate:hover:not(:disabled) {
  background: var(--clipr-purple-gradient) !important;
  color: white !important;
}

.users-list-page.twitch-theme .btn-action-tokens {
  background: rgba(16, 185, 129, 0.2) !important;
  color: #34d399 !important;
}

.users-list-page.twitch-theme .btn-action-tokens:hover:not(:disabled) {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%) !important;
  color: #052e2b !important;
}

.users-list-page.twitch-theme .btn-action-qa {
  background: rgba(99, 102, 241, 0.25) !important;
  color: #a5b4fc !important;
}

.users-list-page.twitch-theme .btn-action-qa:hover {
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%) !important;
  color: #0f172a !important;
}

.users-list-page.twitch-theme .btn-action-edit {
  background: rgba(234, 179, 8, 0.35) !important;
  color: #facc15 !important;
}

.users-list-page.twitch-theme .btn-action-edit:hover {
  background: linear-gradient(135deg, #eab308 0%, #facc15 100%) !important;
  color: #1c1917 !important;
}

.users-list-page.twitch-theme .btn-action-delete {
  background: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
}

.users-list-page.twitch-theme .btn-action-delete:hover {
  background: #dc2626 !important;
  color: white !important;
}

.users-list-page.twitch-theme .users-table-loading i,
.users-list-page.twitch-theme .users-table-empty i {
  color: var(--clipr-text-tertiary) !important;
}

.users-list-page.twitch-theme .users-table-state p,
.users-list-page.twitch-theme .users-table-empty-hint {
  color: var(--clipr-text-secondary) !important;
}

.users-list-page.twitch-theme .onboarding-qa-user-line {
  color: var(--clipr-text-secondary) !important;
}

.users-list-page.twitch-theme .onboarding-qa-question {
  color: var(--clipr-text-tertiary) !important;
}

.users-list-page.twitch-theme .onboarding-qa-answer {
  color: var(--clipr-text-primary) !important;
}

/* Modal Create/Edit y Delete con tema Twitch */
.users-list-page.twitch-theme :deep(.modal-content) {
  background: var(--clipr-bg-card) !important;
  border: 1px solid var(--clipr-border-color) !important;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.85), 0 0 50px var(--clipr-purple-glow) !important;
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.modal-header) {
  border-bottom: 1px solid var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.modal-title) {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.modal-body) {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.modal-body .form-control),
.users-list-page.twitch-theme :deep(.modal-body .border-input) {
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

/* User modal inputs - tema Twitch (más visibles) */
.users-list-page.twitch-theme .user-modal-body :deep(.form-control),
.users-list-page.twitch-theme .user-modal-body :deep(input.form-control),
.users-list-page.twitch-theme .user-modal-body :deep(select.form-control),
.users-list-page.twitch-theme .user-modal-body :deep(.border-input) {
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme .user-modal-body :deep(.control-label) {
  color: var(--clipr-text-secondary) !important;
}

.users-list-page.twitch-theme :deep(.modal-body .form-control:focus),
.users-list-page.twitch-theme :deep(.modal-body .border-input:focus) {
  border-color: var(--clipr-border-purple) !important;
  box-shadow: 0 0 0 0.2rem var(--clipr-purple-glow) !important;
}

.users-list-page.twitch-theme :deep(.modal-body label),
.users-list-page.twitch-theme :deep(.modal-body .control-label) {
  color: var(--clipr-text-secondary) !important;
}

.users-list-page.twitch-theme :deep(.modal-footer) {
  border-top: 1px solid var(--clipr-border-color) !important;
}

.users-list-page.twitch-theme :deep(.modal-footer .btn-primary) {
  background: var(--clipr-purple-gradient) !important;
  box-shadow: 0 8px 30px var(--clipr-purple-glow) !important;
  border: none !important;
  color: white !important;
}

.users-list-page.twitch-theme :deep(.modal-footer .btn-secondary) {
  background: var(--clipr-bg-elevated) !important;
  border: 1px solid var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme :deep(.modal .close) {
  color: var(--clipr-text-secondary) !important;
  opacity: 1;
}

.users-list-page.twitch-theme :deep(.modal .close:hover) {
  color: var(--clipr-text-primary) !important;
  opacity: 1;
}

.users-list-page.twitch-theme :deep(.modal-body select option) {
  background: var(--clipr-bg-elevated) !important;
  color: var(--clipr-text-primary) !important;
}

/* Modal sections - tema Twitch */
.users-list-page.twitch-theme .user-modal-section {
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
}

.users-list-page.twitch-theme .user-modal-section-header {
  background: rgba(168, 85, 247, 0.12) !important;
  color: var(--clipr-purple-light) !important;
  border-bottom: 1px solid var(--clipr-border-color) !important;
}

.users-list-page.twitch-theme .user-modal-section-header i {
  color: var(--clipr-purple-light) !important;
}

.users-list-page.twitch-theme .user-modal-header {
  background: var(--clipr-bg-card) !important;
  border-bottom-color: var(--clipr-border-color) !important;
}

.users-list-page.twitch-theme .user-modal-header .modal-title {
  color: var(--clipr-text-primary) !important;
}

.users-list-page.twitch-theme .user-modal-content {
  border-color: var(--clipr-border-color) !important;
}

.grant-tokens-target {
  color: var(--clipr-text-secondary);
  margin-bottom: 0.8rem;
}

.users-list-page.twitch-theme .grant-tokens-target {
  color: var(--clipr-text-secondary) !important;
}
</style>


